import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getDeviceId, offlineDb } from "@/offline/db";
import { processOutbox, retryFailed } from "@/offline/outbox";
import type { SyncSummary } from "@/types/offline";

interface OfflineContextValue extends SyncSummary {
  deviceId: string;
  setSimulatedOffline: (value: boolean) => void;
  refresh: () => Promise<void>;
  syncNow: () => Promise<void>;
  retry: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextValue | undefined>(undefined);

export const OfflineProvider = ({ children }: { children: React.ReactNode }) => {
  const [simulatedOffline, setSimulatedOfflineState] = useState(() => localStorage.getItem("aczon_simulate_offline") === "true");
  const [summary, setSummary] = useState<SyncSummary>({ drafts: 0, queued: 0, failed: 0, conflicts: 0, online: navigator.onLine, simulatedOffline });
  const deviceId = useMemo(getDeviceId, []);

  const refresh = useCallback(async () => {
    const [drafts, outbox, mediaOutbox, conflicts] = await Promise.all([
      offlineDb.listDrafts(),
      offlineDb.listOutbox(),
      offlineDb.listMediaOutbox(),
      offlineDb.listConflicts(),
    ]);
    const syncedDrafts = drafts.filter((item) => item.state === "SYNCED" && item.syncedAt);
    const lastSyncedAt = syncedDrafts.length > 0 ? syncedDrafts.map((item) => item.syncedAt as string).sort().reverse()[0] : undefined;
    setSummary({
      drafts: drafts.filter((item) => item.state === "DRAFT").length,
      queued:
        outbox.filter((item) => item.state === "QUEUED" || item.state === "PROCESSING").length
        + mediaOutbox.filter((item) => item.state === "QUEUED" || item.state === "PROCESSING").length,
      failed:
        outbox.filter((item) => item.state === "FAILED").length
        + mediaOutbox.filter((item) => item.state === "FAILED").length,
      conflicts: conflicts.filter((item) => item.resolution === "UNRESOLVED").length,
      lastSyncedAt,
      online: navigator.onLine,
      simulatedOffline,
    });
  }, [simulatedOffline]);

  useEffect(() => {
    const handleState = () => void refresh();
    window.addEventListener("online", handleState);
    window.addEventListener("offline", handleState);
    window.addEventListener("aczon:offline-state-change", handleState);
    void refresh();
    return () => { window.removeEventListener("online", handleState); window.removeEventListener("offline", handleState); window.removeEventListener("aczon:offline-state-change", handleState); };
  }, [refresh]);

  useEffect(() => { if (summary.online && !simulatedOffline && summary.queued > 0) void processOutbox().then(refresh); }, [summary.online, summary.queued, simulatedOffline, refresh]);

  const setSimulatedOffline = (value: boolean) => { localStorage.setItem("aczon_simulate_offline", String(value)); setSimulatedOfflineState(value); setSummary((current) => ({ ...current, simulatedOffline: value })); };
  const syncNow = async () => { if (!simulatedOffline && navigator.onLine) await processOutbox(); await refresh(); };
  const retry = async () => { if (!simulatedOffline && navigator.onLine) await retryFailed(); await refresh(); };

  return <OfflineContext.Provider value={{ ...summary, simulatedOffline, deviceId, setSimulatedOffline, refresh, syncNow, retry }}>{children}</OfflineContext.Provider>;
};

export const useOffline = () => {
  const value = useContext(OfflineContext);
  if (!value) throw new Error("useOffline must be used inside OfflineProvider");
  return value;
};
